import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useRestaurant } from '../../context/RestaurantContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Shield, 
  FileText, 
  CheckCircle2, 
  XCircle,
  AlertTriangle,
  Clock,
  Send,
  RefreshCw,
  Settings,
  Search,
  Download,
  ChevronLeft,
  ChevronRight,
  Ban,
  Edit3,
  QrCode,
  ExternalLink,
  TrendingUp,
  AlertCircle
} from 'lucide-react';

const VeriFactuDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { api } = useAuth();
  const { selectedRestaurant } = useRestaurant();
  const restaurantId = selectedRestaurant?.id;
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [errors, setErrors] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showAnularDialog, setShowAnularDialog] = useState(false);
  const [anularReason, setAnularReason] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (restaurantId) {
      fetchData();
    }
  }, [restaurantId, page, statusFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statusRes, invoicesRes, errorsRes] = await Promise.all([
        api.get(`/verifactu/status/${restaurantId}`),
        api.get(`/verifactu/invoices/${restaurantId}?page=${page}&limit=20&status=${statusFilter}`),
        api.get(`/verifactu/errors/${restaurantId}`)
      ]);
      setStatus(statusRes.data);
      setInvoices(invoicesRes.data.invoices);
      setTotalPages(invoicesRes.data.pages);
      setErrors(errorsRes.data.errors);
    } catch (error) {
      console.error('Error fetching VeriFactu data:', error);
      toast.error('Failed to load VeriFactu data');
    } finally {
      setLoading(false);
    }
  };

  const handleAnular = async () => {
    if (!selectedInvoice || !anularReason.trim()) {
      toast.error('Please provide a reason for cancellation');
      return;
    }

    try {
      setProcessing(true);
      await api.post(`/verifactu/invoices/${restaurantId}/anular`, {
        invoice_id: selectedInvoice.id,
        reason: anularReason
      });
      toast.success('Invoice cancelled successfully');
      setShowAnularDialog(false);
      setSelectedInvoice(null);
      setAnularReason('');
      fetchData();
    } catch (error) {
      console.error('Error cancelling invoice:', error);
      toast.error(error.response?.data?.detail || 'Failed to cancel invoice');
    } finally {
      setProcessing(false);
    }
  };

  const handleRetry = async (submissionId) => {
    try {
      await api.post(`/verifactu/submissions/${submissionId}/retry`);
      toast.success('Submission queued for retry');
      fetchData();
    } catch (error) {
      console.error('Error retrying submission:', error);
      toast.error('Failed to retry submission');
    }
  };

  const getStatusBadge = (invoice) => {
    if (invoice.is_anulada) {
      return <Badge variant="destructive" className="bg-red-100 text-red-800">ANULADA</Badge>;
    }
    if (invoice.tipo_factura === 'ANULACION') {
      return <Badge variant="outline" className="border-red-500 text-red-600">ANULACION</Badge>;
    }
    if (invoice.is_rectificativa) {
      return <Badge variant="outline" className="border-blue-500 text-blue-600">RECTIFICATIVA</Badge>;
    }
    
    const statusStyles = {
      pending: 'bg-yellow-100 text-yellow-800',
      sent: 'bg-blue-100 text-blue-800',
      accepted: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
    };
    
    return (
      <Badge className={statusStyles[invoice.aeat_status] || 'bg-gray-100 text-gray-800'}>
        {invoice.aeat_status?.toUpperCase() || 'PENDING'}
      </Badge>
    );
  };

  if (!restaurantId) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Please select a restaurant first</p>
      </div>
    );
  }

  if (loading && !status) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (!status?.enabled) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Shield className="h-6 w-6 text-orange-500" />
              VeriFactu Dashboard
            </h1>
          </div>
        </div>

        <Alert className="bg-amber-50 border-amber-200">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-800">{t('verifactu.settings.notActive')}</AlertTitle>
          <AlertDescription className="text-amber-700">
            {t('verifactu.settings.notActiveDesc')}
          </AlertDescription>
        </Alert>

        <Button onClick={() => navigate('/restaurant/verifactu/settings')} className="bg-orange-500 hover:bg-orange-600">
          <Settings className="h-4 w-4 mr-2" />
          {t('verifactu.settings.enableTitle')}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="h-6 w-6 text-orange-500" />
            {t('verifactu.dashboard.title')}
          </h1>
          <p className="text-gray-500 mt-1">{t('verifactu.dashboard.subtitle')}</p>
        </div>
        <Button onClick={() => navigate('/restaurant/verifactu/settings')} variant="outline">
          <Settings className="h-4 w-4 mr-2" />
          {t('restaurantAdmin.settings')}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('verifactu.dashboard.totalInvoices')}</p>
                <p className="text-2xl font-bold">{status?.total_invoices || 0}</p>
              </div>
              <FileText className="h-8 w-8 text-gray-400" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('verifactu.dashboard.pending')}</p>
                <p className="text-2xl font-bold text-yellow-600">{status?.pending_submissions || 0}</p>
              </div>
              <Clock className="h-8 w-8 text-yellow-400" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('verifactu.dashboard.successRate')}</p>
                <p className="text-2xl font-bold text-green-600">{status?.success_rate_7d || 100}%</p>
              </div>
              <TrendingUp className="h-8 w-8 text-green-400" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('verifactu.dashboard.errorsToday')}</p>
                <p className="text-2xl font-bold text-red-600">{status?.errors_today || 0}</p>
              </div>
              <AlertCircle className="h-8 w-8 text-red-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Chain Status */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            {t('verifactu.dashboard.chainIntegrity')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <Badge className={status?.chain_integrity === 'valid' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
              {status?.chain_integrity === 'valid' ? 'VALID' : 'CHECK REQUIRED'}
            </Badge>
            <span className="text-sm text-gray-500">
              {t('verifactu.dashboard.nextInvoice')}: <span className="font-mono font-medium">A-{String(status?.next_invoice_number || 1).padStart(5, '0')}</span>
            </span>
            {status?.last_accepted && (
              <span className="text-sm text-gray-500">
                {t('verifactu.dashboard.lastAccepted')}: {new Date(status.last_accepted).toLocaleString()}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Errors Alert */}
      {errors.length > 0 && (
        <Alert className="bg-red-50 border-red-200">
          <AlertTriangle className="h-4 w-4 text-red-600" />
          <AlertTitle className="text-red-800">{t('verifactu.dashboard.recentErrors')} ({errors.length})</AlertTitle>
          <AlertDescription className="text-red-700">
            <ul className="mt-2 space-y-1">
              {errors.slice(0, 3).map((error, i) => (
                <li key={i} className="text-sm">
                  Invoice {error.invoice_series}-{error.invoice_number}: {error.error_message || error.error_code}
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* Invoices Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{t('verifactu.dashboard.fiscalInvoices')}</CardTitle>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder={t('verifactu.dashboard.searchInvoices')}
                  className="pl-10 w-48"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <select
                className="border rounded-md px-3 py-2 text-sm"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">{t('verifactu.dashboard.allStatus')}</option>
                <option value="pending">{t('verifactu.dashboard.pending')}</option>
                <option value="accepted">{t('verifactu.dashboard.accepted')}</option>
                <option value="rejected">{t('verifactu.dashboard.rejected')}</option>
              </select>
              <Button variant="outline" size="sm" onClick={fetchData}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {invoices.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {t('verifactu.dashboard.noInvoices')}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-500">{t('verifactu.dashboard.invoice')}</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-500">{t('verifactu.dashboard.date')}</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-500">{t('verifactu.dashboard.type')}</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-500">{t('verifactu.dashboard.base')}</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-500">{t('verifactu.dashboard.iva')}</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-500">{t('verifactu.dashboard.total')}</th>
                      <th className="px-4 py-3 text-center font-medium text-gray-500">{t('verifactu.dashboard.status')}</th>
                      <th className="px-4 py-3 text-center font-medium text-gray-500">{t('verifactu.dashboard.actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {invoices.map((invoice) => (
                      <tr key={invoice.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-mono">
                          {invoice.invoice_series}-{String(invoice.invoice_number).padStart(5, '0')}
                        </td>
                        <td className="px-4 py-3">{invoice.invoice_date}</td>
                        <td className="px-4 py-3">
                          <Badge variant="outline">{invoice.tipo_factura}</Badge>
                        </td>
                        <td className="px-4 py-3 text-right">€{invoice.base_imponible?.toFixed(2)}</td>
                        <td className="px-4 py-3 text-right">€{invoice.cuota_iva?.toFixed(2)}</td>
                        <td className="px-4 py-3 text-right font-medium">€{invoice.total?.toFixed(2)}</td>
                        <td className="px-4 py-3 text-center">
                          {getStatusBadge(invoice)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-1">
                            {invoice.qr_verification_url && !invoice.is_anulada && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => window.open(invoice.qr_verification_url, '_blank')}
                                title="Verify on AEAT"
                              >
                                <QrCode className="h-4 w-4" />
                              </Button>
                            )}
                            {!invoice.is_anulada && invoice.tipo_factura !== 'ANULACION' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedInvoice(invoice);
                                  setShowAnularDialog(true);
                                }}
                                title="Cancel Invoice"
                              >
                                <Ban className="h-4 w-4 text-red-500" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-gray-500">
                    Page {page} of {totalPages}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Anular Dialog */}
      <Dialog open={showAnularDialog} onOpenChange={setShowAnularDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ban className="h-5 w-5 text-red-500" />
              {t('verifactu.anular.title')}
            </DialogTitle>
            <DialogDescription>
              {t('verifactu.anular.description')}{' '}
              <span className="font-mono font-medium">
                {selectedInvoice?.invoice_series}-{String(selectedInvoice?.invoice_number).padStart(5, '0')}
              </span>
              . {t('verifactu.anular.warning')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="text-gray-500">{t('verifactu.dashboard.total')}:</div>
                <div className="font-medium">€{selectedInvoice?.total?.toFixed(2)}</div>
                <div className="text-gray-500">{t('verifactu.dashboard.date')}:</div>
                <div>{selectedInvoice?.invoice_date}</div>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">{t('verifactu.anular.reason')} *</label>
              <Input
                placeholder={t('verifactu.anular.reasonPlaceholder')}
                value={anularReason}
                onChange={(e) => setAnularReason(e.target.value)}
                data-testid="anular-reason-input"
              />
            </div>

            <Alert className="bg-amber-50 border-amber-200">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-700 text-sm">
                {t('verifactu.anular.cannotUndo')}
              </AlertDescription>
            </Alert>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAnularDialog(false)}>
              {t('common.cancel') || 'Cancel'}
            </Button>
            <Button
              variant="destructive"
              onClick={handleAnular}
              disabled={processing || !anularReason.trim()}
              data-testid="confirm-anular-btn"
            >
              {processing ? '...' : t('verifactu.anular.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VeriFactuDashboard;
